import { useAtom } from "jotai";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import API from "../api/api/API";
import { tenantAtom } from "../atoms/GlobalAtoms";
import useUser from "../api/api-hooks/auth/useUser";
import { getUserCloudPermissions } from "../utils/UserUtils";
import { PermissionsData } from "../types/GlobalInterfaces";

const startingCloudPermissions = {
  cloud_get_latest_schema: false,
  cloud_get_all_people: false,
  cloud_create_person: false,
  cloud_create_cloud_app: false,
  cloud_create_global_quick_create: false,
  cloud_delete_person: false,
  cloud_resend_invitation: false,
  cloud_modify_permissions: false,
  cloud_assign_ownership: false,
  cloud_trigger_backup: false,
  cloud_trigger_backup_in_an_interval_time: false,
  cloud_delete_and_trigger_backup: false,
  cloud_delete_module_and_then_backup: false,
  cloud_get_records: false,
  cloud_get_record: false,
  cloud_create_record: false,
  cloud_modify: false,
  cloud_modify_cloud_app: false,
  cloud_modify_search: false,
  cloud_modify_record: false,
  cloud_merge_records: false,
  cloud_delete_record: false,
  cloud_get_CSVTemplate: false,
  cloud_import_CSV: false,
  cloud_upload_file: false,
  cloud_get_file: false,
  cloud_create_module: false,
  cloud_modify_module: false,
  cloud_delete_module: false,
  cloud_create_field: false,
  cloud_modify_field: false,
  cloud_delete_field: false,
  cloud_modify_template: false,
  cloud_get_apps: false,
  cloud_install_app: false,
  cloud_get_credentials: false,
  cloud_create_credential: false,
  cloud_update_credential: false,
  cloud_delete_credential: false,
  cloud_modify_sidebar: false,
  cloud_get_webhooks: false,
  cloud_create_webhook: false,
  cloud_modify_webhook: false,
  cloud_delete_webhook: false,
  cloud_get_tags: false,
  cloud_create_tag: false,
  cloud_modify_tag: false,
  cloud_delete_tag: false,
  cloud_modify_record_tags: false,
  cloud_create_workspace: false,
  cloud_modify_workspace: false,
  cloud_delete_workspace: false,
  cloud_create_page: false,
  cloud_delete_page: false,
  cloud_modify_page: false,
  cloud_transfer_page: false,
  cloud_duplicate_page: false,
  cloud_get_page: false,
  cloud_get_pages: false,
  cloud_create_duplicate_page: false,
  cloud_modify_page_settings: false,
  cloud_get_workspace: false,
  cloud_get_workspaces: false,
  cloud_get_all_workspaces: false,
  cloud_join_workspace: false,
  cloud_leave_workspace: false,
  cloud_unpublish_cloud_app: false,
  cloud_duplicate_folder: false,
  cloud_get_workflow: false,
  cloud_create_workflow: false,
  cloud_update_workflow: false,
  cloud_delete_workflow: false,
  cloud_get_cloud_apps: false,
  cloud_get_cloud_app: false,
  cloud_delete_cloud_app: false,
  cloud_module_import: false,
  cloud_module_export: false,
  cloud_lock_records: false,
  cloud_unlock_records: false,
  cloud_delete_global_quick_create: false,
  cloud_update_global_quick_create: false,
  cloud_create_global_layout: false,
  cloud_delete_global_layout: false,
  cloud_update_global_layout: false,
  cloud_get_indexes: false,
  cloud_crud_indexes: false,
  cloud_modify_deleted_records: false,
};

export type CloudPermissions = typeof startingCloudPermissions;

const startingPermisions = (): CloudPermissions => {
  return { ...startingCloudPermissions };
};

const useCloudPermissions = (tenantID: string | null = null) => {
  const [tenant] = useAtom(tenantAtom);
  const { user, cPermissions: permissions } = useUser();
  const isSharedView = window.location.pathname.startsWith("/shared-view");

  const userTenantID = tenant?.TenantID;

  const cloudPermissions = useMemo(() => {
    const scopeMap = startingPermisions();
    const currentTenantID = tenantID || tenant?.TenantID;
    if (!currentTenantID || !permissions || !user) {
      return { cPermissions: scopeMap, cPermissionProfile: null };
    }

    const permissionProfile: PermissionsData | undefined =
      getUserCloudPermissions(user, permissions, userTenantID) as any;

    const scopes = permissionProfile?.Scopes;

    if (!scopes) {
      return { cPermissions: scopeMap, cPermissionProfile: null };
    }

    for (const scope of scopes) {
      scopeMap[scope as keyof CloudPermissions] = true;
    }

    return { cPermissions: scopeMap, cPermissionProfile: permissionProfile };
  }, [permissions, user, userTenantID, tenantID]);

  const cloudPermissionsTenant = useQuery({
    queryKey: ["cloudPermissions", tenantID],
    queryFn: () => {
      return API.getCloudTenantByID(tenantID ?? "").then((res) => {
        const scopeMap = startingPermisions();

        const permissionProfile: PermissionsData | undefined =
          getUserCloudPermissions(
            user,
            res.data.data.profiles,
            tenantID
          ) as any;

        permissionProfile?.Scopes?.forEach((scope: string) => {
          scopeMap[scope as keyof CloudPermissions] = true;
        });

        return scopeMap;
      });
    },
    enabled: !!tenantID && !isSharedView,
  });

  return {
    cPermissions: isSharedView
      ? startingCloudPermissions
      : cloudPermissions.cPermissions,
    cPermissionProfile: cloudPermissions.cPermissionProfile,
    cPermissionsTenant: cloudPermissionsTenant.data,
    cPermissionsLoading: cloudPermissionsTenant.isLoading,
  };
};

export default useCloudPermissions;
